/**
 * Batched Update Engine
 *
 * Handles batching of form data updates to prevent excessive re-renders.
 * Collects multiple field updates and applies them in a single state update.
 */

import { FieldPath, BatchedUpdate } from './types';

/**
 * Applies a value to a nested object at a specific path
 *
 * @param obj - The object to modify
 * @param path - The path to the value (using dot notation)
 * @param value - The value to set
 * @param separator - The separator used in the path
 * @returns A new object with the value applied
 */
export function setValueAtPath(
  obj: any,
  path: FieldPath,
  value: any,
  separator: string,
): any {
  if (!path) {
    return value;
  }

  const segments = path.split(separator);
  const newObj = Array.isArray(obj) ? [...obj] : { ...obj };
  let current = newObj;

  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    const nextSegment = segments[i + 1];

    // Determine if the next level should be an array or object
    const isArrayIndex = /^\d+$/.test(nextSegment);

    if (current[segment] === undefined || current[segment] === null) {
      current[segment] = isArrayIndex ? [] : {};
    } else {
      current[segment] = Array.isArray(current[segment])
        ? [...current[segment]]
        : { ...current[segment] };
    }

    current = current[segment];
  }

  const lastSegment = segments[segments.length - 1];
  current[lastSegment] = value;

  return newObj;
}

/**
 * Gets a value from a nested object at a specific path
 *
 * @param obj - The object to read from
 * @param path - The path to the value
 * @param separator - The separator used in the path
 * @returns The value at the path, or undefined
 */
export function getValueAtPath(
  obj: any,
  path: FieldPath,
  separator: string,
): any {
  if (!path) {
    return obj;
  }

  const segments = path.split(separator);
  let current = obj;

  for (const segment of segments) {
    if (current === null || current === undefined) {
      return undefined;
    }
    current = current[segment];
  }

  return current;
}

/**
 * Applies a batched update to form data
 *
 * @param formData - The current form data
 * @param batchedUpdate - The batched update to apply
 * @param separator - The separator used in field paths
 * @returns New form data with all updates applied
 */
export function applyBatchedUpdate(
  formData: any,
  batchedUpdate: BatchedUpdate,
  separator: string,
): any {
  let updatedData = formData;

  for (const update of batchedUpdate.updates) {
    updatedData = setValueAtPath(
      updatedData,
      update.path,
      update.value,
      separator,
    );
  }

  return updatedData;
}

/**
 * Batched Update Manager
 *
 * Manages a queue of pending updates and applies them in batches.
 * Uses microtasks to batch all synchronous updates within the same event loop tick.
 */
export class BatchedUpdateManager {
  private pendingUpdates: Map<FieldPath, any> = new Map();
  private microtaskScheduled = false;
  private readonly onBatchReady: (batch: BatchedUpdate) => void;

  constructor(onBatchReady: (batch: BatchedUpdate) => void) {
    this.onBatchReady = onBatchReady;
  }

  /**
   * Queues a field update for batching
   *
   * @param fieldPath - The field to update
   * @param value - The new value
   */
  queueUpdate(fieldPath: FieldPath, value: any): void {
    this.pendingUpdates.set(fieldPath, value);

    // Batch all synchronous updates via microtask
    // This allows all queueUpdate calls in the same sync block to be collected
    if (!this.microtaskScheduled) {
      this.microtaskScheduled = true;
      queueMicrotask(() => {
        this.microtaskScheduled = false;
        this.flush();
      });
    }
  }

  /**
   * Flushes all pending updates
   */
  flush(): void {
    if (this.pendingUpdates.size === 0) {
      return;
    }

    const updates = Array.from(this.pendingUpdates.entries()).map(
      ([path, value]) => ({
        path,
        value,
      }),
    );

    this.pendingUpdates.clear();

    this.onBatchReady({ updates });
  }

  /**
   * Clears all pending updates without applying them
   */
  clear(): void {
    this.pendingUpdates.clear();
  }
}
